import React, { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Building2, BookOpen, CheckCircle2, X, Plus, Pencil, Trash2, Award, ExternalLink, Sparkles } from "lucide-react";

export default function DepartmentHub() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isEditor = ["hr", "admin", "manager"].includes(user?.role);
  const isAdminLike = ["hr", "admin"].includes(user?.role);
  const [departments, setDepartments] = useState([]);
  const [diagrams, setDiagrams] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [attempts, setAttempts] = useState([]);

  const load = async () => {
    const [d, diag, att] = await Promise.all([
      api.get("/departments"),
      api.get("/department-diagrams"),
      api.get("/quiz-attempts").catch(() => ({ data: [] })),
    ]);
    setDepartments(d.data);
    setDiagrams(diag.data);
    setAttempts(att.data);
  };

  useEffect(() => { load(); }, []);

  const passedDepts = new Set(attempts.filter((a) => a.passed).map((a) => a.department_id));

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><Building2 size={22} /></div>
          <div>
            <h1 className="sw-heading text-[28px]">{t("dh.title")}</h1>
            <p className="text-[14px] text-[#747474]">{t("dh.subtitle")}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {departments.map((d) => {
          const diag = diagrams.find((x) => x.department_id === d.id);
          const passed = passedDepts.has(d.id);
          return (
            <button
              key={d.id}
              data-testid={`dept-card-${d.id}`}
              onClick={() => setSelected(d)}
              className="sw-card text-left hover:shadow-md transition-all flex flex-col gap-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-[#475569] font-semibold">{d.cost_code || "—"}</div>
                  <div className="font-medium text-[16px] text-[#1e293b] mt-1">{d.name}</div>
                </div>
                {passed && <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />}
              </div>
              <div className="text-[12px] text-[#475569] flex-1">
                {diag?.title ? diag.title : <span className="text-[#94a3b8] italic">{t("dh.no_diagram")}</span>}
              </div>
              <div className="flex items-center gap-2 text-[11px] text-[#94a3b8] pt-2 border-t border-[#f1f5f9]">
                {diag ? (
                  <span className="inline-flex items-center gap-1"><BookOpen size={11} /> Diagram available</span>
                ) : (
                  <span className="inline-flex items-center gap-1"><BookOpen size={11} /> {t("dh.not_configured")}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {selected && (
        <DepartmentModal
          department={selected}
          diagram={diagrams.find((x) => x.department_id === selected.id)}
          attempts={attempts.filter((a) => a.department_id === selected.id)}
          isEditor={isEditor}
          isAdminLike={isAdminLike}
          onClose={() => setSelected(null)}
          onEdit={() => setEditing({ department: selected, diagram: diagrams.find((x) => x.department_id === selected.id) })}
          onEditQuiz={() => setEditingQuiz({ department: selected })}
          onReload={async () => { await load(); }}
        />
      )}

      {editing && (
        <EditDiagramModal
          department={editing.department}
          diagram={editing.diagram}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
      {editingQuiz && (
        <EditQuizModal
          department={editingQuiz.department}
          onClose={() => setEditingQuiz(null)}
          onSaved={async () => { setEditingQuiz(null); await load(); }}
        />
      )}
    </div>
  );
}

function DepartmentModal({ department, diagram, attempts, isEditor, isAdminLike, onClose, onEdit, onEditQuiz, onReload }) {
  const { t } = useI18n();
  const [quiz, setQuiz] = useState(null);
  const [taking, setTaking] = useState(false);
  useEffect(() => {
    api.get(`/departments/${department.id}/quiz`).then((r) => setQuiz(r.data)).catch(() => setQuiz(null));
  }, [department.id]);

  const myBest = attempts.reduce((b, a) => (a.score_pct > (b?.score_pct ?? -1) ? a : b), null);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="department-modal" className="bg-white rounded-[20px] max-w-[720px] w-full p-0 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 bg-gradient-to-br from-[#1e293b] via-[#475569] to-[#334155] text-white rounded-t-[20px]">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-white/70">{department.cost_code || "Department"}</div>
              <h3 className="text-[22px] font-semibold mt-1">{department.name}</h3>
              {diagram?.title && <div className="text-[14px] text-white/80 mt-1">{diagram.title}</div>}
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="department-modal-close"><X size={20} /></button>
          </div>
        </div>

        <div className="p-6">
          {diagram?.description && <p className="text-[13.5px] text-[#475569] mb-3">{diagram.description}</p>}
          {diagram?.image_url && <img src={diagram.image_url} alt="Diagram" className="rounded-[12px] border border-[#e2e8f0] mb-3 max-h-[360px]" />}
          {diagram?.content && (
            <div className="text-[13.5px] text-[#1e293b] whitespace-pre-line border-l-2 border-[#475569] pl-4 my-3">{diagram.content}</div>
          )}
          {diagram?.document_url && (
            <a href={diagram.document_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[13px] text-[#475569] hover:underline">
              <ExternalLink size={14} /> Open attached document
            </a>
          )}
          {!diagram?.title && !diagram?.content && !diagram?.image_url && (
            <div className="text-[13px] text-[#94a3b8] italic">{t("dh.no_diagram_dept")}</div>
          )}

          {isEditor && (
            <div className="flex gap-2 mt-4 pt-4 border-t border-[#e2e8f0]">
              <button data-testid="edit-diagram-btn" onClick={onEdit} className="sw-btn-secondary !text-[12px]"><Pencil size={12} />  {t("dh.edit_diagram")}</button>
              {isAdminLike && <button data-testid="edit-quiz-btn" onClick={onEditQuiz} className="sw-btn-secondary !text-[12px]"><Award size={12} />  {t("dh.edit_quiz")}</button>}
            </div>
          )}

          {quiz?.questions?.length > 0 && (
            <div className="mt-5 pt-4 border-t border-[#e2e8f0]">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-[#475569] font-semibold">Onboarding quiz</div>
                  <div className="text-[12px] text-[#94a3b8]">{quiz.questions.length} question(s) · pass at {quiz.pass_score_pct}%</div>
                </div>
                {myBest && (
                  <div className={`text-[11px] px-2 py-1 rounded-full ${myBest.passed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    Last: {myBest.score_pct}% · {myBest.passed ? "passed" : "failed"}
                  </div>
                )}
              </div>
              {!taking ? (
                <button data-testid="take-quiz-btn" onClick={() => setTaking(true)} className="sw-btn-primary"><Sparkles size={13} /> {myBest ? "Retry quiz" : "Take quiz"}</button>
              ) : (
                <QuizRunner
                  department={department}
                  quiz={quiz}
                  onDone={async () => { setTaking(false); await onReload(); }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QuizRunner({ department, quiz, onDone }) {
  const { t } = useI18n();
  const [answers, setAnswers] = useState(Array(quiz.questions.length).fill(-1));
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const canSubmit = answers.every((a) => a >= 0);
  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/departments/${department.id}/quiz/submit`, { answers });
      setResult(r.data);
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
    finally { setSubmitting(false); }
  };

  if (result) {
    return (
      <div className={`rounded-[14px] p-4 border ${result.passed ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
        <div className="flex items-center gap-2">
          <CheckCircle2 size={18} className={result.passed ? "text-emerald-600" : "text-amber-600"} />
          <div className="font-semibold text-[#1e293b]">{result.passed ? "Passed!" : "Not yet"} · {result.score_pct}%</div>
        </div>
        <div className="text-[12px] text-[#475569] mt-1">{result.correct}/{result.total} correct answers · pass threshold {quiz.pass_score_pct}%</div>
        <button onClick={onDone} className="sw-btn-secondary mt-3 !text-[12px]">{t("common.close")}</button>
      </div>
    );
  }

  return (
    <div data-testid="quiz-runner" className="rounded-[14px] border border-[#e2e8f0] p-4 bg-[#f8fafc] flex flex-col gap-4">
      {quiz.questions.map((q, qi) => (
        <div key={q.id || qi}>
          <div className="text-[13px] font-medium text-[#1e293b] mb-2">{qi + 1}. {q.text}</div>
          <div className="flex flex-col gap-1.5">
            {q.options.map((opt, oi) => (
              <label key={oi} data-testid={`quiz-q${qi}-opt${oi}`} className={`flex items-center gap-2 px-3 py-2 rounded-[10px] border cursor-pointer text-[13px] ${answers[qi] === oi ? "border-[#475569] bg-white" : "border-transparent bg-white hover:border-[#e2e8f0]"}`}>
                <input type="radio" name={`q${qi}`} checked={answers[qi] === oi} onChange={() => { const a = [...answers]; a[qi] = oi; setAnswers(a); }} />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
      <button data-testid="submit-quiz" disabled={!canSubmit || submitting} onClick={submit} className="sw-btn-primary self-end">{submitting ? "..." : "Submit answers"}</button>
    </div>
  );
}

function EditDiagramModal({ department, diagram, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    title: diagram?.title || department.name,
    description: diagram?.description || "",
    content: diagram?.content || "",
    image_url: diagram?.image_url || "",
    document_url: diagram?.document_url || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/departments/${department.id}/diagram`, form);
      onSaved();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[20px] max-w-[600px] w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-4"><h3 className="sw-heading text-[18px]">Edit diagram · {department.name}</h3><button onClick={onClose}><X size={20} /></button></div>
        <div className="flex flex-col gap-3">
          <div><label className="sw-label">Title *</label><input data-testid="diagram-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="sw-input" /></div>
          <div><label className="sw-label">Short description</label><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sw-input" /></div>
          <div><label className="sw-label">Content (Markdown / plain text)</label><textarea data-testid="diagram-content" rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="sw-input" /></div>
          <div><label className="sw-label">Image URL (org chart, workflow, etc.)</label><input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="sw-input" placeholder="https://..." /></div>
          <div><label className="sw-label">Attached document URL (PDF)</label><input value={form.document_url} onChange={(e) => setForm({ ...form, document_url: e.target.value })} className="sw-input" placeholder="https://..." /></div>
        </div>
        <div className="flex justify-end gap-2 mt-4"><button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button><button data-testid="save-diagram" onClick={save} disabled={saving || !form.title} className="sw-btn-primary">{saving ? "..." : "Save"}</button></div>
      </div>
    </div>
  );
}

function EditQuizModal({ department, onClose, onSaved }) {
  const { t } = useI18n();
  const [questions, setQuestions] = useState([]);
  const [passScore, setPassScore] = useState(70);
  const [onboardingReq, setOnboardingReq] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/departments/${department.id}/quiz?include_answers=true`).then((r) => {
      setQuestions(r.data.questions?.length ? r.data.questions : [{ text: "", options: ["", ""], correct_index: 0 }]);
      setPassScore(r.data.pass_score_pct || 70);
      setOnboardingReq(r.data.onboarding_required !== false);
    });
  }, [department.id]);

  const addQ = () => setQuestions([...questions, { text: "", options: ["", ""], correct_index: 0 }]);
  const updateQ = (qi, patch) => setQuestions(questions.map((q, i) => i === qi ? { ...q, ...patch } : q));
  const addOpt = (qi) => updateQ(qi, { options: [...questions[qi].options, ""] });
  const setOpt = (qi, oi, val) => updateQ(qi, { options: questions[qi].options.map((o, i) => i === oi ? val : o) });
  const removeOpt = (qi, oi) => updateQ(qi, {
    options: questions[qi].options.filter((_, i) => i !== oi),
    correct_index: questions[qi].correct_index >= oi && questions[qi].correct_index > 0 ? questions[qi].correct_index - 1 : questions[qi].correct_index,
  });
  const removeQ = (qi) => setQuestions(questions.filter((_, i) => i !== qi));

  const save = async () => {
    const cleanQuestions = questions
      .filter((q) => q.text.trim() && q.options.filter((o) => o.trim()).length >= 2)
      .map((q) => ({ ...q, options: q.options.map((o) => o.trim()) }));
    if (cleanQuestions.length === 0) { alert(t("dh.quiz_min")); return; }
    setSaving(true);
    try {
      await api.put(`/departments/${department.id}/quiz`, {
        questions: cleanQuestions,
        pass_score_pct: parseInt(passScore),
        onboarding_required: onboardingReq,
      });
      onSaved();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[20px] max-w-[720px] w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-4"><h3 className="sw-heading text-[18px]">Edit quiz · {department.name}</h3><button onClick={onClose}><X size={20} /></button></div>
        <div className="flex items-center gap-4 mb-4 pb-3 border-b border-[#e2e8f0]">
          <div className="flex-1">
            <label className="sw-label">Pass score (%)</label>
            <input data-testid="quiz-pass-score" type="number" min="1" max="100" value={passScore} onChange={(e) => setPassScore(e.target.value)} className="sw-input !py-1.5 !text-[13px] w-[120px]" />
          </div>
          <label className="flex items-center gap-2 text-[12px] text-[#1e293b] cursor-pointer">
            <input data-testid="quiz-onboarding-req" type="checkbox" checked={onboardingReq} onChange={(e) => setOnboardingReq(e.target.checked)} />
            Required for new hires
          </label>
        </div>
        <div className="flex flex-col gap-4">
          {questions.map((q, qi) => (
            <div key={qi} className="rounded-[14px] border border-[#e2e8f0] p-3 bg-[#f8fafc]">
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">Question {qi + 1}</span>
                {questions.length > 1 && <button onClick={() => removeQ(qi)} className="text-[#881a27]"><Trash2 size={13} /></button>}
              </div>
              <input data-testid={`q-text-${qi}`} placeholder={t("dh.question_ph")} value={q.text} onChange={(e) => updateQ(qi, { text: e.target.value })} className="sw-input mb-2" />
              <div className="flex flex-col gap-1.5">
                {q.options.map((o, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <input type="radio" data-testid={`q-correct-${qi}-${oi}`} name={`correct-${qi}`} checked={q.correct_index === oi} onChange={() => updateQ(qi, { correct_index: oi })} />
                    <input data-testid={`q-opt-${qi}-${oi}`} placeholder={`Option ${oi + 1}${q.correct_index === oi ? " (correct)" : ""}`} value={o} onChange={(e) => setOpt(qi, oi, e.target.value)} className="sw-input flex-1 !py-1.5 !text-[13px]" />
                    {q.options.length > 2 && <button onClick={() => removeOpt(qi, oi)} className="text-[#94a3b8]"><X size={14} /></button>}
                  </div>
                ))}
              </div>
              <button onClick={() => addOpt(qi)} className="text-[11px] text-[#475569] hover:underline mt-2"><Plus size={11} className="inline" />  {t("dh.add_option")}</button>
            </div>
          ))}
        </div>
        <button data-testid="add-question" onClick={addQ} className="sw-btn-secondary mt-3 !text-[12px]"><Plus size={12} />  {t("dh.add_question")}</button>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-[#e2e8f0]">
          <button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
          <button data-testid="save-quiz" onClick={save} disabled={saving} className="sw-btn-primary">{saving ? "..." : "Save quiz"}</button>
        </div>
      </div>
    </div>
  );
}
